import { Divider, Grid, Paper, Switch, Typography } from "@material-ui/core";
import { Field, FormikProps } from "formik";
import React, { useState } from "react";
import { t } from "ttag";
import { languages } from "../../constants/languages";
import { CoolSwitch, Select } from '@coolremote/shared';
import { Arrow as SvgArrow } from "../../icons";
import { useStoreState } from "../../models/RootStore";
import { IUser } from "../../models/Users";
import PhoneField from "./PhoneField";
import UserDetailsField from "./UserDetailsField";
import useStyles from "./UserDetailsMyProfile.style";
import SwitchField from "./SwitchField";
import { clsx } from "clsx";
import { ExpandMore } from "@material-ui/icons";
import { passwordRuleHint } from "../../services/formService";

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;
interface IProps extends Omit<FormikProps<any>, "handleSubmit"> {
  user: IUser;
  errors: any;
  touched: any;
  withPasswordField?: boolean;
  withChangePasswordField?: boolean;
  temperatureScale: any;
  pressureScale: any;
  invite?: boolean;
  inviteEdit?: boolean;
  showLanguage?: boolean;
  setPasswordOpen?: any;
  passwordOpen?: boolean;
  toggleCustomersNicknamesDialog?: any;
  toggleDataAndPrivacyDialog?: any;
}

const UserDetailsMyProfile: React.FC<IProps> = ({
  user,
  errors,
  touched,
  withPasswordField,
  withChangePasswordField,
  showLanguage = false,
  values,
  setFieldValue,
  temperatureScale,
  pressureScale,
  invite = false,
  inviteEdit = false,
  setPasswordOpen,
  passwordOpen,
  toggleCustomersNicknamesDialog,
  toggleDataAndPrivacyDialog,
  ...props
}) => {
  const classes = useStyles();

  const types = useStoreState((s) => s.types);
  const configFile = useStoreState((s) => s.config);
  const { disable2FA } = configFile || {};

  const [isAlertsGroupVisible, setIsAlertsGroupVisible] = useState<boolean>(false);

  const languagesObj = Object.keys(languages).map((language: any) => ({ value: language, label: languages[language] }));

  const toggleAlertsGroupVisibility = () => {
    setIsAlertsGroupVisible(!isAlertsGroupVisible);
  }

  const handleAlertLogUnitDisplayChange = (checked: any) => {
    setFieldValue("alertLogUnitDisplay", checked ? "nameAndControlId" : "unitName");
  }

  return (
    <Grid className={classes.personalDetailsContainer}>
      <Grid container className={classes.dialogSubContent}>
        <Grid item xs={5}>
          <Paper className={classes.leftCard} elevation={0}>
            <>
              <Typography className={classes.headerStyle}>{t`Personal Details`}</Typography>
              <Divider className={classes.divider} orientation="horizontal" />
              <Field
                disabled={inviteEdit}
                label={t`First name*`}
                value={values.firstName}
                name="firstName"
                component={UserDetailsField}
                error={errors.firstName && touched.firstName ? true : false}
                helperText={
                  errors.firstName && touched.firstName ? errors.firstName : t`Up to 20 characters`
                }
              />
              <Field
                disabled={inviteEdit}
                label={t`Last name*`}
                value={values.lastName}
                name="lastName"
                component={UserDetailsField}
                error={errors.lastName && touched.lastName ? true : false}
                helperText={errors.lastName && touched.lastName ? errors.lastName : t`Up to 20 characters`}
              />
              <Field
                label={t`Username/Email`}
                disabled={true}
                value={values.username}
                name="username"
                component={UserDetailsField}
                error={errors.username && touched.username ? true : false}
                helperText={
                  errors.username && touched.username ? errors.username : t`6-20 characters, must be unique`
                }
              />
              {withPasswordField && (
                <Field
                  label={t`Password`}
                  value={values.password}
                  name="password"
                  component={UserDetailsField}

                  error={withPasswordField && errors.password && touched.password ? true : false}
                  helperText={
                    errors.password && touched.password
                      ? errors.password
                      : passwordRuleHint()
                  }
                />
              )}
              <Field
                label={t`Telephone`}
                value={values.phone}
                name="phone"
                component={PhoneField}
                onChange={(e: any) => setFieldValue("phone", e)}
                error={errors.phone && touched.phone ? true : false}
                helperText={
                  errors.phone && touched.phone ? errors.phone : t`International format telephone number`
                }
              />
              <Divider className={classes.divider} orientation="horizontal" />
            </>
            <>
              <div className={classes.deleteAccountWrap} onClick={() => toggleDataAndPrivacyDialog(true)}>
                <Typography className={classes.titleStyle}>{t`Data & privacy`}</Typography>
                <SvgArrow />
              </div>
            </>
          </Paper>
        </Grid>

        <Grid item xs={7}>
          <Paper className={classes.rightCard} elevation={0}>
            <Typography className={classes.headerStyle}>{t`User Preferences`}</Typography>
            <Divider className={classes.divider} orientation="horizontal" />

            <SwitchField
            name="temperatureScale"
            label={t`Temperature Units`}
            optionOffText={t`°C`}
            optionOnText={t`°F`}
            onChange={(checked:any) =>setFieldValue("temperatureScale", checked ? 2 : 1)}
            checked={temperatureScale[values.temperatureScale] === "fahrenheit"}
            />


          <SwitchField
            name="measurementUnits"
            label={t`Measurement Units`}
            optionOffText={t`kg/cm2`}
            optionOnText={t`PSI`}
            onChange={(checked:any) =>setFieldValue("measurementUnits", checked ? 2 : 1)}
            checked={pressureScale[values.measurementUnits] === "PSI"}
            />


          <SwitchField
            name="timeFormat"
            label={t`Time Format`}
            optionOnText={types.timeFormat[1]?.text}
            optionOffText={types.timeFormat[0]?.text}
            onChange={(checked:any) => setFieldValue("timeFormat", !checked ? types.timeFormat[0].value : types.timeFormat[1]?.value)}
            checked={values.timeFormat === types.timeFormat[1].value}
            />

           <SwitchField
            name="dateFormat"
            label={t`Date Format`}
            optionOnText={types.dateFormat[1]?.text}
            optionOffText={types.dateFormat[0]?.text}
            onChange={(checked:any) => setFieldValue("dateFormat", !checked ? types.dateFormat[0].value : types.dateFormat[1]?.value)}
            checked={values.dateFormat === types.dateFormat[1].value}
            />

            <SwitchField
            name="lockItemsReorder"
            label={t`Enable unit/group reorder`}
            optionOnText={t`Enabled`}
            optionOffText={t`Lock`}
            onChange={(checked:any) => setFieldValue("lockItemsReorder", !checked)}
            checked={!values.lockItemsReorder}
            />

            <SwitchField
            name="alertLogUnitDisplay"
            label={t`Alert Log Unit Display`}
            optionOffText={t`Unit Name`}
            optionOnText={t`Unit Name (Address)`}
            onChange={handleAlertLogUnitDisplayChange}
            checked={values.alertLogUnitDisplay === "nameAndControlId"}
            />


          <div className={clsx(classes.alertGroups ,isAlertsGroupVisible ? classes.alertsGroupVisible : classes.alertsGroupHidden)}>

          <div className={classes.alertsGroupHeader} onClick={toggleAlertsGroupVisibility}>
          <div className={clsx(classes.alertsGroupArrow , isAlertsGroupVisible ? classes.alertsGroupArrowExpanded : classes.alertsGroupArrowCollapsed) }><ExpandMore /></div>
          <Typography>{t`Hide Alerts From Dashboard`}</Typography>
          </div>

          <SwitchField
            name="showClearFilterAlerts"
            label={t`Hide clean filter alerts`}
            optionOffText={t`Show`}
            optionOnText={t`Hide`}
            onChange={(checked:any) => setFieldValue("showClearFilterAlerts", !checked)}
            checked={!values.showClearFilterAlerts}
            />

            <SwitchField
            name="showScheduleFailureAlerts"
            label={t`Hide schedule failure alerts`}
            optionOffText={t`Show`}
            optionOnText={t`Hide`}
            onChange={(checked:any) => setFieldValue("showScheduleFailureAlerts", !checked)}
            checked={!values.showScheduleFailureAlerts}
            />

            <SwitchField
            name="showSmartRuleAlerts"
            label={t`Hide smart rule alerts`}
            optionOffText={t`Show`}
            optionOnText={t`Hide`}
            onChange={(checked:any) => setFieldValue("showSmartRuleAlerts", !checked)}
            checked={!values.showSmartRuleAlerts}
            />

            <SwitchField
            name="showUserActionAlerts"
            label={t`Hide user action alerts`}
            optionOffText={t`Show`}
            optionOnText={t`Hide`}
            onChange={(checked:any) => setFieldValue("showUserActionAlerts", !checked)}
            checked={!values.showUserActionAlerts}
            />

            <SwitchField
            name="showSmsLimitAlerts"
            label={t`Hide SMS limit reached alerts`}
            optionOffText={t`Show`}
            optionOnText={t`Hide`}
            onChange={(checked:any) => setFieldValue("showSmsLimitAlerts", !checked)}
            checked={!values.showSmsLimitAlerts}
            />

          </div>


            {
              showLanguage && (
                <div className={classes.newBlock}>
                  <Divider className={classes.divider} orientation="horizontal" />
                  <div className={classes.language}>
                    <Typography className={classes.languageTitle}>{t`Language`}</Typography>
                    <Select
                      fullWidth
                      className={classes.input}
                      classes={{ trigger: classes.languageTrigger }}
                      value={`${values.language}`}
                      onChange={(val: any) => setFieldValue("language", val)}
                      options={languagesObj}
                    />
                  </div>
                </div>
              )
            }

            <div className={classes.newBlock}>
              <Divider className={classes.divider} orientation="horizontal" />
              {!disable2FA && <div className={classes.authFactorContainer}>
                <Grid container component="label" direction="row" alignItems="center">
                  <Grid item className={classes.itemTitle} xs={6} md={10}>
                    <div style={{ alignSelf: "center" }}>
                      <Typography>{t`Enable email 2-factor-authentication`}</Typography>
                    </div>
                  </Grid>
                  <Grid item className={classes.authFactorItemSwtich} xs={6} md={2}>
                    <Grid item className={classes.tempFontStyle}>
                      <Field
                        name="is2FA"
                        value={values.is2FA}
                        as={() => (
                          <CoolSwitch
                            color={"default"}
                            switchChange={() => {
                              setFieldValue("is2FA", !values.is2FA);
                            }}
                            checked={values.is2FA}
                            name="is2FA"
                            classes={{
                              thumb: classes[values?.is2FA ? "thumb" : "thumbDisabled"],
                              track: classes[values?.is2FA ? "track" : "trackDisabled"]
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </div>}
              <div className={classes.flexColumn}>

                <div className={classes.changePassword} onClick={() => setPasswordOpen(!passwordOpen)}>
                  <Typography className={classes.passwordTitle}>
                    {t`Change Password`}
                  </Typography>
                  <SvgArrow />
                </div>
                <div className={classes.changePassword} onClick={() => toggleCustomersNicknamesDialog(true)}>
                  <Typography className={classes.passwordTitle}>
                    {t`Customers Nicknames`}
                  </Typography>
                  <SvgArrow />
                </div>

              </div>

            </div>
          </Paper>
        </Grid>
      </Grid>
    </Grid>

  );
};

export default UserDetailsMyProfile;
